import { useCallback, useMemo } from 'react';
import { addDayOffset, minDate, thisMonthDate, thisSundayDate, todayDate } from '../../../common/src/date_string.ts';
import {
  ClientOrg,
  Org,
  OrgBase,
  OrgId,
  OrgType,
  schoolOrgTypes,
  SongListId,
  SongListType,
  SongSlug,
} from '../../../common/src/model.ts';
import { getSchoolTermDateRange } from '../../../common/src/org.ts';
import { hasRepeatingRow } from '../../../common/src/repeating_rows.ts';
import * as server_api from '../../../common/src/server_api.ts';
import { OneHourMs } from '../../../common/src/sleep.ts';

import { useOnline } from '../util/use_online.tsx';
import { localStorageGet, LocalStorageKey, localStorageRemove, localStorageSet } from './client_local_storage.ts';
import { SongListTimestamp } from './idb.ts';
import {
  clearPassedSundaysWrittenDownPending,
  fetchPassedSundays,
  getPassedSundaysWrittenDownToSend,
  isPassedSundaysWrittenDownPending,
  passedSundaysWriteDownsSettled,
  sendPassedSundaysWrittenDown,
  storeAllSongListsWrittenThrough,
} from './passed_sundays_written_through.ts';
import { addToSongListIdMap, mapSongListId } from './song_list_id_map.tsx';
import {
  classifySongListSyncFailure,
  SongListSaveRefusal,
  SongListSyncFailure,
  SongListSyncRetry,
} from './song_list_sync_retry.ts';
import { getWeeklySongLists } from './song_lists.ts';
import {
  clearAllSongListUpdates,
  currentSongListUpdateTimestampInitialized,
  DeletableSongList,
  getCurrentSongListUpdateTimestamp,
  getSongListUpdatesBefore,
  removeSongListUpdatesBefore,
  replaceQueuedSongListIds,
  songListUpdatesPending,
} from './song_updates.ts';
import { synchronizeHouseholdWithServer, useHousehold } from './use_household.tsx';
import { useLocalStorage } from './use_local_storage.tsx';
import { getUser } from './use_user.tsx';

export type { SongListSaveRefusal } from './song_list_sync_retry.ts';

export const testOrgIds = [5, 243];
export const songFutureSizeInWeeks = 8;

export function getCurrentOrg() {
  const orgs = localStorageGet(LocalStorageKey.CachedOrgs);
  const currentOrgId = localStorageGet(LocalStorageKey.CurrentOrgId);
  const household = localStorageGet(LocalStorageKey.Household);
  return getCurrentOrgFromOrgs(orgs, household?.orgIds ?? [], currentOrgId);
}

export function useCurrentOrg(): {
  currentOrg: ClientOrg | undefined;
  setCurrentOrg: (org: OrgBase | undefined) => void;
  householdOrgs: ClientOrg[];
  setHouseholdOrg: (org: OrgBase) => Promise<void>;
  clearHouseholdOrgTypes: (orgTypes: OrgType[]) => Promise<void>;
  clearAllHouseholdOrgs: () => Promise<void>;
  // TODO(hewitt): this function should take a callback that accepts the current org & returns a partial
  updateOrgInfo: (org: { id: OrgId } & Partial<Org>) => Promise<void>;
} {
  const { household, setHousehold } = useHousehold();
  const [cachedOrgs, setCachedOrgs] = useLocalStorage(LocalStorageKey.CachedOrgs);
  const [currentOrgId, setCurrentOrgId] = useLocalStorage(LocalStorageKey.CurrentOrgId);
  const isOnline = useOnline();

  const currentOrg = useMemo(
    () => getCurrentOrgFromOrgs(cachedOrgs, household?.orgIds ?? [], currentOrgId),
    [cachedOrgs, currentOrgId, household],
  );

  const setHouseholdOrgIds = useCallback(
    async (orgIds: OrgId[]) => {
      if (!isOnline) {
        throw new Error('An Internet connection is required.');
      }
      // pending org updates are lost when switching orgs
      clearOrgUpdates();
      await clearPendingSongListWork();
      await setHousehold((value) => {
        if (!value) {
          console.error('ERROR: Household should have been set before setting household org');
          return;
        }
        return { ...value, orgIds };
      });
      await synchronizeOrgsWithServer({ force: true }); // triggers household sync
    },
    [isOnline, setHousehold],
  );

  const setCurrentOrg = useCallback(
    (org: OrgBase | undefined) => {
      if (org) {
        setCachedOrgs((orgs) => [...orgs.filter(({ id }) => id !== org.id), org]);
      }
      setCurrentOrgId(org?.id);
      clearOrgSongLists();
    },
    [setCurrentOrgId, setCachedOrgs],
  );

  const setHouseholdOrg = useCallback(
    async (org: OrgBase) => {
      const otherOrgIds =
        household?.orgIds.filter((id) => {
          if (id === org.id) {
            return false;
          }
          const otherOrg = cachedOrgs.find((otherOrg) => otherOrg.id === id);
          if (!otherOrg) {
            return false;
          }
          const { type } = otherOrg;
          return schoolOrgTypes.includes(org.type) ? !schoolOrgTypes.includes(type) : schoolOrgTypes.includes(type);
        }) ?? [];
      setCurrentOrg(org);
      await setHouseholdOrgIds([...otherOrgIds, org.id]);
    },
    [cachedOrgs, household, setCurrentOrg, setHouseholdOrgIds],
  );

  const clearHouseholdOrgTypes = useCallback(
    async (orgTypes: OrgType[]) => {
      const remainingOrgIds =
        household?.orgIds.filter((id) => {
          const otherOrg = cachedOrgs.find((otherOrg) => otherOrg.id === id);
          return otherOrg && !orgTypes.includes(otherOrg.type);
        }) ?? [];
      if (currentOrgId && !remainingOrgIds.includes(currentOrgId)) {
        setCurrentOrgId(undefined);
        clearOrgSongLists();
      }
      await setHouseholdOrgIds(remainingOrgIds);
    },
    [cachedOrgs, currentOrgId, household?.orgIds, setCurrentOrgId, setHouseholdOrgIds],
  );

  const clearAllHouseholdOrgs = useCallback(async () => {
    await setHouseholdOrgIds([]);
    setCurrentOrgId(undefined);
    clearOrgSongLists();
  }, [setCurrentOrgId, setHouseholdOrgIds]);

  const updateOrgInfo = useCallback(
    async (updates: { id: OrgId } & Partial<Org>) => {
      if (!currentOrg) {
        console.error('ERROR: Attempt to update non-existent organization');
        return;
      }
      // Merged into the cached org as stored now, not as this render saw it: two calls from one callback would
      // otherwise each start from the same stale org, and the second would undo the first's fields. The setter
      // runs the update at once, so `changed` is known before the sync.
      let changed = false;
      setCachedOrgs((prevOrgs) => {
        const org = prevOrgs.find(({ id }) => id === updates.id) ?? currentOrg;
        const previousUpdates = org.updates ?? {};
        const mergedUpdates = { ...previousUpdates, ...updates };
        if (JSON.stringify(previousUpdates) === JSON.stringify(mergedUpdates)) {
          return prevOrgs;
        }
        changed = true;
        const otherOrgs = prevOrgs.filter(({ id }) => id != updates.id);
        return [...otherOrgs, { ...org, ...updates, updates: mergedUpdates }];
      });
      if (changed) {
        await synchronizeOrgsWithServer();
      }
    },
    [currentOrg, setCachedOrgs],
  );

  const householdOrgs = cachedOrgs.filter(({ id }) => household?.orgIds && household.orgIds.includes(id));

  return {
    currentOrg,
    setCurrentOrg,
    householdOrgs,
    setHouseholdOrg,
    clearAllHouseholdOrgs,
    clearHouseholdOrgTypes,
    updateOrgInfo,
  };
}

export async function refreshSingingPlans(forOrgId: OrgId) {
  try {
    await server_api.generateOrgSingingPlans({ forDate: todayDate(), forOrgId });
    await server_api.generateOrgSingingPlans({ forDate: addDayOffset(todayDate(), 1), forOrgId });
  } catch (e: any) {
    console.error(`Failed to refresh singing plans ${e.message}`);
  }
}

export function clearOrgUpdates() {
  const cachedOrgs = localStorageGet(LocalStorageKey.CachedOrgs);
  const clearedOrgs = cachedOrgs.map((org) => {
    const { updates: _, ...orgNoChanges } = org;
    return orgNoChanges;
  });
  if (JSON.stringify(clearedOrgs) === JSON.stringify(cachedOrgs)) {
    return;
  }
  localStorageSet(LocalStorageKey.CachedOrgs, clearedOrgs);
}

// How long a sync waits for its predecessor (an org push phase, a weekly-list run) before running anyway: the requests
// have no timeout, so one that never settles would otherwise hold up every later sync for the session
export const syncPredecessorWaitMs = 30_000;

/** Resolves when `predecessor` settles or after `waitMs`, whichever comes first. Never rejects. */
async function waitAtMost(predecessor: Promise<unknown>, waitMs: number) {
  let timer: ReturnType<typeof setTimeout> | undefined;
  await Promise.race([
    predecessor.catch(() => {}),
    new Promise<void>((resolve) => (timer = setTimeout(resolve, waitMs))),
  ]);
  clearTimeout(timer);
}

// Each org fetch is numbered as it starts, and the last one whose answer was stored is remembered: an earlier-started
// fetch never stores over a later one's (issue #4213). A forced sync from a live update reaches the saving admin's own
// client, whose next edit's sync can push, refetch and store the saved org while the forced fetch is still in flight;
// the forced fetch's answer, older than the edit, must not then replace it. Overlapping syncs only: a lone sync stores
// as it always has.
let orgFetchesStarted = 0;
let lastStoredOrgFetch = 0;

// Every push of an org's queued updates is counted as it starts and again as it settles, and each org remembers the
// count at its last push event. A fetch notes the count as it starts and keeps the local copy of any org with a push
// event since: the server may have read that org before the push landed (issue #4213). Counting the settle too covers a
// push already in flight when the fetch started, whose updates its own sync then cleared.
let orgPushEvents = 0;
const lastOrgPushEvent = new Map<OrgId, number>();

function noteOrgPushEvent(orgId: OrgId) {
  lastOrgPushEvent.set(orgId, ++orgPushEvents);
}

// The push phase in progress or last finished: one runs at a time (issue #4213), so two overlapping syncs never send
// different snapshots of one org's queued updates, an older one able to land last and undo the newer. Each phase reads
// the cache once it has its turn, so it sends everything pushed or merged before it. A phase that waits past
// syncPredecessorWaitMs for a hung push runs beside it: it re-sends the hung push's fields with any newer ones (they
// stay queued until that push answers), and only if the hung push then lands after it can the server take that push's
// older values for the fields changed since. The fetches that follow the pushes overlap freely; the guards above order
// what they store.
let orgPushPhase: Promise<void> = Promise.resolve();

/** Pushes every org's queued updates, one push phase at a time; true when one was saved. */
async function pushOrgUpdates(): Promise<boolean> {
  const predecessor = orgPushPhase;
  let finish = () => {};
  orgPushPhase = new Promise((resolve) => (finish = resolve));
  try {
    await waitAtMost(predecessor, syncPredecessorWaitMs);
    return await pushQueuedOrgUpdates();
  } finally {
    finish();
  }
}

async function pushQueuedOrgUpdates(): Promise<boolean> {
  const pushed = localStorageGet(LocalStorageKey.CachedOrgs)
    .map(({ updates }) => updates)
    .filter((updates) => !!updates);
  let orgContainsChanges = false;
  try {
    for (const updates of pushed) {
      noteOrgPushEvent(updates.id);
      try {
        await server_api.upsertOrg(updates);
      } finally {
        noteOrgPushEvent(updates.id);
      }
      orgContainsChanges = true;
    }
  } catch (error: any) {
    // if not authorized, just ignore to prevent the app from getting stuck on a server permission error: the queued
    // updates are dropped as if pushed, the refused org's and those of any org after it alike
    if (!error.message.includes('403')) {
      throw error;
    }
  }
  clearPushedOrgUpdates(pushed);
  return orgContainsChanges;
}

/**
 * Drops each org's queued updates if they are still the ones pushed. An edit merged in meanwhile (updateOrgInfo) keeps
 * them, the pushed fields with the new ones, for its own sync to push: sending the pushed fields again is the same
 * partial upsert.
 */
function clearPushedOrgUpdates(pushed: Array<NonNullable<ClientOrg['updates']>>) {
  const pushedById = new Map(pushed.map((updates) => [updates.id, JSON.stringify(updates)]));
  const cachedOrgs = localStorageGet(LocalStorageKey.CachedOrgs);
  let cleared = false;
  const clearedOrgs = cachedOrgs.map((org) => {
    if (!org.updates || pushedById.get(org.id) !== JSON.stringify(org.updates)) {
      return org;
    }
    cleared = true;
    const { updates: _, ...orgNoChanges } = org;
    return orgNoChanges;
  });
  if (cleared) {
    localStorageSet(LocalStorageKey.CachedOrgs, clearedOrgs);
  }
}

// syncs once per hour unless forced, pushing, or the org is missing
export async function synchronizeOrgsWithServer({ force }: { force?: boolean } = {}) {
  // ignore network errors
  try {
    // if there are pending household changes, sync them first in case it affects the currently selected church
    const pushHousehold = localStorageGet(LocalStorageKey.PushHousehold);
    if (pushHousehold) {
      await synchronizeHouseholdWithServer({ force });
    }

    // remove org along with household
    const household = localStorageGet(LocalStorageKey.Household);
    if (!household?.token) {
      localStorageRemove(LocalStorageKey.CachedOrgs);
      await clearPendingSongListWork();
      return;
    }

    // with nothing queued there is nothing to push, and no turn to wait for: an edit merged in later has its own sync
    const orgContainsChanges = localStorageGet(LocalStorageKey.CachedOrgs).some(({ updates }) => updates)
      ? await pushOrgUpdates()
      : false;

    const mostRecentSyncTimestamp = localStorageGet(LocalStorageKey.MostRecentOrgSyncTimestamp);
    if (
      !force &&
      !pushHousehold &&
      !orgContainsChanges &&
      mostRecentSyncTimestamp &&
      Date.now() - mostRecentSyncTimestamp < OneHourMs
    ) {
      return;
    }

    const fetchNumber = ++orgFetchesStarted;
    const pushEventsAtFetchStart = orgPushEvents;
    const serverOrgs = await server_api.getHouseholdOrgs(household.token);
    if (fetchNumber < lastStoredOrgFetch) {
      // a fetch started after this one has stored its answer, which this older one may predate (an edit pushed and
      // refetched while it was in flight). Nothing is stored, the timestamp included: that later fetch set it when it
      // stored, so returning here is simplest and the hourly clock is no less right
      return;
    }
    lastStoredOrgFetch = fetchNumber;
    // Read again after the fetch: an edit made meanwhile (updateOrgInfo) is stored with its `updates`, or was pushed
    // since this fetch started, and the server's copy may predate it. Such an org keeps its local copy; the sync that
    // pushed it, or will, refetches (issue #4213).
    const cachedOrgs = localStorageGet(LocalStorageKey.CachedOrgs);
    const editedOrgIds = cachedOrgs
      .filter(({ id, updates }) => updates || (lastOrgPushEvent.get(id) ?? 0) > pushEventsAtFetchStart)
      .map(({ id }) => id);
    const fetchedOrgs = serverOrgs.filter(({ id }) => !editedOrgIds.includes(id));
    if (serializeOrgs(cachedOrgs) !== serializeOrgs(serverOrgs)) {
      // update/append new server org info (do not remove in case we switch orgs more than once on a slow network)
      localStorageSet(LocalStorageKey.CachedOrgs, [
        ...cachedOrgs.filter(({ id }) => !fetchedOrgs.map(({ id }) => id).includes(id)),
        ...fetchedOrgs,
      ]);
    }
    localStorageSet(LocalStorageKey.MostRecentOrgSyncTimestamp, Date.now());
  } catch (e: any) {
    console.error(`Failed to sync church with server: ${e.message}`);
  }
}

function serializeOrgs(org: ClientOrg[]): string {
  // avoid taking into account the sync timestamp and change flag when comparing org contents
  return JSON.stringify(
    org.map(({ mostRecentSyncTimestamp: _unused1, updates: _unused2, ...sanitizedOrg }) => sanitizedOrg),
  );
}

// The weekly-list sync started last, until it settles: one runs at a time (issue #4213). A run reads the org its
// predecessors cached, so one started while a forced org sync is in flight cannot download for the stale org and
// store its lists after the forced run's. Never rejects: synchronizeWeeklySongListsNow catches its own errors.
let runningWeeklySongListSync: Promise<void> | undefined;
// The run waiting for runningWeeklySongListSync to start: every call made meanwhile joins it, so a burst of messages
// costs at most one run in flight and one queued. Read `forceOrgSync` only as it starts: a forced call that joins
// makes it forced.
let queuedWeeklySongListSync: { promise: Promise<void>; forceOrgSync: boolean } | undefined;

// Each run is numbered as it starts its work, and the last run that stored its lists is remembered: a predecessor
// that outlasted syncPredecessorWaitMs may finish after a later run, with lists computed for the org as it was
// before, and must not store them over that run's. Only the WeeklySongList store is guarded. The org it reads is
// guarded by synchronizeOrgsWithServer's own numbering; the removals when there is no current org read the org as
// cached now, not as the run began; and SchoolTerms is also written by the song-list sync and the term planner,
// unordered against this chain, and a school's terms do not depend on the org a run fetched.
let weeklySongListRunsStarted = 0;
let lastStoredWeeklySongListRun = 0;

// `forceOrgSync` fetches the org even within the hour, for a live update saying the org itself changed (issue
// #4213); the lists are then downloaded for the org as fetched, so a new repeating row widens the window at once.
// A call made while a run is in progress queues one run behind it, which starts when that one is done or after
// syncPredecessorWaitMs; a call made while a run is queued joins it. The promise resolves when the run this call
// started or joined is done.
export function synchronizeWeeklySongLists({ forceOrgSync = false }: { forceOrgSync?: boolean } = {}): Promise<void> {
  if (queuedWeeklySongListSync) {
    queuedWeeklySongListSync.forceOrgSync ||= forceOrgSync;
    return queuedWeeklySongListSync.promise;
  }
  const predecessor = runningWeeklySongListSync;
  if (!predecessor) {
    return startWeeklySongListSync(forceOrgSync);
  }
  const queued = { promise: Promise.resolve(), forceOrgSync };
  queued.promise = (async () => {
    await waitAtMost(predecessor, syncPredecessorWaitMs);
    queuedWeeklySongListSync = undefined;
    await startWeeklySongListSync(queued.forceOrgSync);
  })();
  queuedWeeklySongListSync = queued;
  return queued.promise;
}

function startWeeklySongListSync(forceOrgSync: boolean): Promise<void> {
  const run: Promise<void> = synchronizeWeeklySongListsNow({ forceOrgSync }).finally(() => {
    if (runningWeeklySongListSync === run) {
      runningWeeklySongListSync = undefined;
    }
  });
  runningWeeklySongListSync = run;
  return run;
}

async function synchronizeWeeklySongListsNow({ forceOrgSync = false }: { forceOrgSync?: boolean }) {
  const run = ++weeklySongListRunsStarted;
  try {
    await synchronizeOrgsWithServer({ force: forceOrgSync });
    const currentOrg = getCurrentOrg();

    if (!currentOrg) {
      localStorageRemove(LocalStorageKey.SchoolTerms);
      localStorageRemove(LocalStorageKey.WeeklySongList);
      return;
    }

    await synchronizeSchoolTerms(currentOrg);
    // how far the church's passed Sundays are written down, fetched before the lists (see fetchPassedSundays) and read
    // with these lists only, so the weekly lists go on filling a passed Sunday the server has not written yet (issue
    // #4192). It is not stored: the planner reads only the date its own lists downloaded with.
    const passedSundays = await fetchPassedSundays(currentOrg);
    const songLists = await getSongLists(currentOrg);
    const weeklySongLists = getWeeklySongLists(currentOrg, songLists, passedSundays?.writtenThrough);

    // a later-started run has stored; or the household switched orgs during this run's requests, which cleared the
    // lists (clearOrgSongLists), and these are for the org it left
    if (run < lastStoredWeeklySongListRun || getCurrentOrg()?.id !== currentOrg.id) {
      return;
    }
    // claimed even when nothing changed: what is stored is then this run's answer
    lastStoredWeeklySongListRun = run;
    // only update storage if something changed. Read now, not as the run began: a predecessor past the wait may have
    // stored other lists during this run's requests, and this run's answer must then replace them
    const storedSongsForWeek = localStorageGet(LocalStorageKey.WeeklySongList);
    if (JSON.stringify(weeklySongLists) !== JSON.stringify(storedSongsForWeek)) {
      localStorageSet(LocalStorageKey.WeeklySongList, weeklySongLists);
    }
  } catch (e: any) {
    console.error(`Failed to sync weekly song lists: ${e.message}`);
  }
}

export async function synchronizeSchoolTerms(school?: OrgBase) {
  try {
    if (!school || school.type !== OrgType.School) {
      localStorageRemove(LocalStorageKey.SchoolTerms);
      return;
    }
    const previousSchoolTerms = localStorageGet(LocalStorageKey.SchoolTerms);
    const schoolTerms = await server_api.getCustomSongIntervals(school.id);
    if (JSON.stringify(schoolTerms) !== JSON.stringify(previousSchoolTerms)) {
      localStorageSet(LocalStorageKey.SchoolTerms, schoolTerms);
    }
  } catch (e: any) {
    console.error(`Failed to sync school terms: ${e.message}`);
  }
}

// note that school terms must be synchronized first if requesting for a school
export async function getSongLists(church: Pick<Org, 'id' | 'type' | 'churchLiturgySchema'>) {
  let fromDate = todayDate();
  if (church.type === OrgType.School) {
    const schoolTerms = localStorageGet(LocalStorageKey.SchoolTerms);
    if (schoolTerms.length !== 0) {
      const termDateRange = getSchoolTermDateRange(schoolTerms, todayDate());
      if (termDateRange) {
        fromDate = termDateRange.start;
      }
    }
  } else {
    fromDate = thisMonthDate(thisSundayDate(todayDate()));
  }
  // be sure to go at least one week back to include events
  return await server_api.getSongLists({
    churchId: church.id,
    from: minDate([fromDate, addDayOffset(todayDate(), -7)]),
    // a repeating row fills a Sunday from any earlier one (issue #4191), so its church also reads every earlier
    // Sunday's list (and no other earlier list). Writing passed Sundays down (#4192) does not make that unnecessary:
    // a By-Sunday row's previous Sunday of the same ordinal may fall before the month's first, and the Sundays that
    // passed before #4192, or before a row's repeat applied (repeatFrom), were never filled, so a source may lie
    // weeks back
    ...(hasRepeatingRow(church) && { includeEarlierSundays: true }),
  });
}

// The sync pass or discard in progress: only one runs at a time (issue #3609). Cleared by
// finishSongListSyncWork in the same synchronous step that reads synchronizeRequested, so no request is lost.
let songListSyncWork: Promise<void> | undefined;
// a sync was requested while songListSyncWork was running: run another pass as soon as it finishes
let synchronizeRequested = false;
const songListSyncRetry = new SongListSyncRetry();

/**
 * Uploads the pending song list updates, then downloads the server's lists. Called after every local edit
 * and when a page that shows song lists opens: it starts the refusal backoff over and tries now.
 */
export async function synchronizeAllSongLists() {
  songListSyncRetry.resetBackoff();
  await runSongListSync();
}

/** The admin asked to try saving again: start the backoff over and try now. */
export async function retrySongListSync() {
  await synchronizeAllSongLists();
}

/** Set while the server refuses the content of the pending song list updates (issue #3609). */
export function getSongListSaveRefusal(): SongListSaveRefusal | undefined {
  return songListSyncRetry.refusal;
}

/**
 * Throws the pending song list updates away and takes the server's lists for the current church, e.g.
 * after the server has refused them. Waits for a sync pass in progress to finish first, so an upload
 * running now cannot schedule a retry or set a refusal after the discard. Rejects if the download fails
 * (the updates are gone either way; the next sync downloads the lists).
 */
export async function discardPendingSongListUpdates() {
  while (songListSyncWork) {
    await songListSyncWork.catch(() => {});
  }
  songListSyncWork = discardPass();
  await songListSyncWork;
}

async function discardPass() {
  synchronizeRequested = false;
  songListSyncRetry.cancelRetry();
  songListSyncRetry.recordSuccess();
  try {
    await clearPendingSongListWork();
    const currentOrg = getCurrentOrg();
    if (currentOrg) {
      await downloadServerSongLists(currentOrg);
    }
  } finally {
    await finishSongListSyncWork(0);
  }
}

async function runSongListSync() {
  if (songListSyncWork) {
    // e.g. an edit made while a sync is uploading: sync again as soon as this one finishes
    synchronizeRequested = true;
    return;
  }
  songListSyncWork = songListSyncPass();
  await songListSyncWork;
}

async function songListSyncPass() {
  synchronizeRequested = false;
  songListSyncRetry.cancelRetry();
  let retryDelayMs = 0;
  // only the upload can be refused: a 4xx from the org sync, the download or school terms is transient
  let uploading = false;
  try {
    await synchronizeOrgsWithServer();
    const currentOrg = getCurrentOrg();
    const household = localStorageGet(LocalStorageKey.Household);
    if (!currentOrg || !household) {
      localStorageRemove(LocalStorageKey.AllSongLists);
      localStorageRemove(LocalStorageKey.AllSongListsWrittenThrough);
      localStorageRemove(LocalStorageKey.SchoolTerms);
      await clearPendingSongListWork();
      songListSyncRetry.recordSuccess();
      return;
    }
    uploading = true;
    await uploadUpdatedSongLists(currentOrg);
    uploading = false;
    songListSyncRetry.recordSuccess();
    if (!(await songListSyncWorkPending())) {
      await downloadServerSongLists(currentOrg, songListSyncWorkPending);
    }
    await synchronizeSchoolTerms(currentOrg);
  } catch (error: unknown) {
    retryDelayMs = songListSyncRetry.recordFailure(await classifyFailure(error, uploading));
  } finally {
    await finishSongListSyncWork(retryDelayMs);
  }
}

/** Ends every sync pass and discard: frees the slot, schedules the next pass if needed, notifies listeners. */
async function finishSongListSyncWork(retryDelayMs: number) {
  try {
    const pending = await songListSyncWorkPending();
    songListSyncWork = undefined;
    if (pending || synchronizeRequested) {
      songListSyncRetry.scheduleRetry(synchronizeRequested ? 0 : retryDelayMs, () => void runSongListSync());
    }
  } finally {
    songListSyncWork = undefined;
    invokeSongListSyncListeners();
  }
}

async function classifyFailure(error: unknown, duringUpload: boolean): Promise<SongListSyncFailure> {
  const failure = classifySongListSyncFailure(error);
  if (failure.kind === 'forbidden') {
    // user is not authorized - prevent the app from getting stuck on a server permission error
    try {
      await clearPendingSongListWork();
      const currentOrg = getCurrentOrg();
      if (currentOrg) {
        await downloadServerSongLists(currentOrg);
      }
    } catch (e: unknown) {
      console.error(`Failed to download song lists after a 403: ${(e as Error)?.message}`);
    }
    return failure;
  }
  if (failure.kind === 'refused' && !duringUpload) {
    // a later request (org sync, download, school terms) failed: the server refused no song list update
    return { kind: 'transient' };
  }
  return failure;
}

const songListSyncListeners: Array<() => void> = [];

export function addSongListSyncListener(listener: () => void) {
  songListSyncListeners.push(listener);
}

export function removeSongListSyncListener(listener: () => void) {
  const index = songListSyncListeners.indexOf(listener);
  if (index !== -1) {
    songListSyncListeners.splice(index, 1);
  }
}

function invokeSongListSyncListeners() {
  for (const listener of songListSyncListeners) {
    listener();
  }
}

// What a sync pass uploads: the song list updates queued, and how far the planner wrote the church's passed Sundays
// down itself (getPassedSundaysWrittenDownToSend), which may be pending alone (a write-down that wrote no list, or
// whose lists went up ahead of it). Either one keeps the pass retrying and holds the download back, which would
// otherwise store the server's older date with lists that do not hold the planner's writes yet; and either is a
// change not yet saved (useAllSongLists' containsUpdates).
export async function songListSyncWorkPending(): Promise<boolean> {
  return isPassedSundaysWrittenDownPending() || (await songListUpdatesPending());
}

// The pending song list updates are thrown away, and with them how far the planner wrote the Sundays down that they
// hold: sent alone, it would have the server leave those Sundays unwritten. A write-down whose `write` is running
// meanwhile then stores no mark either (writePassedSundaysDown).
async function clearPendingSongListWork() {
  clearPassedSundaysWrittenDownPending();
  await clearAllSongListUpdates();
}

async function uploadUpdatedSongLists(org: ClientOrg) {
  // How far the planner wrote the church's passed Sundays down (issue #4209) rides inside this pass's list requests,
  // never ahead of its lists: the pass waits until the timestamp is past every key queued before this page loaded and
  // no write-down of this tab is in progress, and then reads the timestamp and the mark with no await between, so
  // every list the mark covers is below the timestamp, in this pass, or an earlier pass uploaded it
  // (getPassedSundaysWrittenDownToSend). A write-down never waits for a pass, so the wait always ends. A failed read
  // of the timestamp is tried again, and a second failure fails the pass, which retries with the backoff.
  await currentSongListUpdateTimestampInitialized();
  await passedSundaysWriteDownsSettled();
  const currentTimestamp = getCurrentSongListUpdateTimestamp();
  const writtenDown = getPassedSundaysWrittenDownToSend(org);
  // An id the server reassigned on an earlier upload (see replaceSongListIds) is rewritten in the queue and the cache
  // when the answer comes, and the planner's writes map a render-time id through the same map (useAllSongLists'
  // withCurrentId). Mapping here too is a safety net for any other writer that queues an old id, for deletes and
  // upserts alike, so it reaches the server's row.
  const songLists = await getSongListUpdatesBefore(currentTimestamp, { mapId: mapSongListId });
  const updatedLists = songLists.filter((songList) => !songList.isDeleted);
  // A list never uploaded keeps its temporary negative id and has nothing to delete on the server.
  const deletedIds = songLists
    .filter((songList) => songList.isDeleted && songList.id !== undefined && songList.id > 0)
    .map(({ id }) => id!);
  // with no list request to carry it, the mark goes alone (a write-down that left every list as it was, or whose
  // lists an earlier pass uploaded)
  if (deletedIds.length === 0 && updatedLists.length === 0) {
    await sendPassedSundaysWrittenDown(org, writtenDown);
    await removeSongListUpdatesBefore(currentTimestamp);
    return;
  }
  // Deletes go first. A church's Sunday cleared to nothing is deleted (issue #4190), and a song set on that Sunday
  // before this upload is queued as a new list without a server id: upserted first, it would land on the deleted
  // list's row through the server's one-list-per-date conflict target, and the delete would then remove the new song.
  // A delete only removes a list and never creates an overlap, so no upsert in the same batch depends on running
  // before it.
  // The mark goes with the first request, so it is stored before any of the pass's lists, and with the POST too: the
  // server stores it once and never moves it earlier, so the second copy changes nothing when the first was stored,
  // and the POST's lists still come with their mark when it was not (the DELETE answered by an old server during a
  // deploy, say). A pass with no mark sends the requests as before.
  const { mark } = writtenDown;
  if (deletedIds.length > 0) {
    await server_api.deleteSongLists(org.id, deletedIds, mark);
  }
  if (updatedLists.length > 0) {
    const newIds = await server_api.upsertSongLists(org.id, removeNegativeIds(updatedLists), mark);
    // Every id the server answered differently is reassigned: a temporary id gets its server id, and a church's
    // Sunday sent under an id that no longer exists (deleted by the one-time sweep or by another device) is saved
    // under the row its Sunday has now (issue #4190). An answer of 0 means "deleted on the server" (issue #3609) and
    // reassigns nothing.
    const newIdsByOldId = new Map<SongListId, SongListId>();
    for (const [index, songList] of updatedLists.entries()) {
      const newId = newIds[index];
      if (songList.id && newId && newId > 0 && newId !== songList.id) {
        newIdsByOldId.set(songList.id, newId);
      }
    }
    if (newIdsByOldId.size > 0) {
      await replaceSongListIds({ newIds: newIdsByOldId, fromTimestamp: currentTimestamp });
    }
  }
  writtenDown.sent();
  await removeSongListUpdatesBefore(currentTimestamp);
}

/**
 * The server saved each old id's list (a key of newIds) under its new id: rewrite the old ids in the cached lists the
 * planner edits and in the updates queued while the upload ran, in one pass each, so a later edit or delete names the
 * server's row even after a reload (the cache keeps the old id until the next download, which is skipped while edits
 * are pending). The id map serves the open event editor and a planner write from a render that still showed an old id.
 *
 * The id map and the cache change together, with no await between: a planner write landing in between would map its
 * render-time id to the new one and store its list under it beside the cached old id, and the cache rewrite would then
 * drop that edited copy as the server's older one.
 */
async function replaceSongListIds({
  newIds,
  fromTimestamp,
}: {
  newIds: Map<SongListId, SongListId>;
  fromTimestamp: SongListTimestamp;
}) {
  for (const [oldId, newId] of newIds) {
    addToSongListIdMap(oldId, newId);
  }
  replaceCachedSongListIds(newIds);
  await replaceQueuedSongListIds({ newIds, fromTimestamp });
}

function replaceCachedSongListIds(newIds: Map<SongListId, SongListId>) {
  const cachedLists = localStorageGet(LocalStorageKey.AllSongLists);
  const newIdOf = (id: SongListId | undefined) => (id === undefined ? undefined : newIds.get(id));
  const replacedIds = new Set(cachedLists.map(({ id }) => newIdOf(id)).filter((id) => id !== undefined));
  if (replacedIds.size === 0) {
    return;
  }
  // a list already cached under a new id is the server's older copy of the list just saved: the saved one replaces it
  localStorageSet(
    LocalStorageKey.AllSongLists,
    cachedLists
      .filter(({ id }) => id === undefined || !replacedIds.has(id))
      .map((songList) => {
        const newId = newIdOf(songList.id);
        return newId === undefined ? songList : { ...songList, id: newId };
      }),
  );
}

function removeNegativeIds(songLists: DeletableSongList[]): DeletableSongList[] {
  return songLists.map((songList) => (songList.id && songList.id >= 0 ? songList : { ...songList, id: undefined }));
}

async function downloadServerSongLists(church: ClientOrg, cancel?: () => Promise<boolean>) {
  // how far the church's passed Sundays are written down, fetched before the lists (see fetchPassedSundays) and stored
  // with them, so the planner reads it only with the lists it downloaded with (issue #4192)
  const passedSundays = await fetchPassedSundays(church);
  const allSongs = await server_api.getSongLists({ churchId: church.id });
  const isInternalUser = !!getUser()?.internal;
  const seasonalSongLists = isInternalUser ? await server_api.getSeasonalSongLists() : [];

  // Toss the results at the last minute if song list updates are pending because otherwise the pending changes
  // disappear from LocalStorageKey.AllSongLists and therefore from the liturgy planner.
  // We will eventually update our local storage to match the server, but only after all the updates have
  // been updated.
  if (!cancel || !(await cancel())) {
    localStorageSet(LocalStorageKey.AllSongLists, [...allSongs, ...seasonalSongLists]);
    storeAllSongListsWrittenThrough(church.id, passedSundays);
  }
}

export function setSongTempoForOrg({
  org,
  songSlug,
  songTempo,
}: {
  org: ClientOrg;
  songSlug: SongSlug;
  songTempo: number;
}): Pick<ClientOrg, 'id' | 'songTempos'> {
  const songTempos = { ...org.songTempos, [songSlug]: songTempo };
  return { id: org.id, songTempos };
}

export function setSongTranspositionForOrg({
  org,
  songSlug,
  modulationWholeSteps,
}: {
  org: ClientOrg;
  songSlug: SongSlug;
  modulationWholeSteps: number;
}): { id: OrgId } & Pick<Org, 'songTranspositions'> {
  const songTranspositions = { ...org.songTranspositions };
  delete songTranspositions[songSlug];
  if (modulationWholeSteps !== 0) {
    songTranspositions[songSlug] = modulationWholeSteps;
  }
  return { id: org.id, songTranspositions };
}

function getCurrentOrgFromOrgs(
  orgs: ClientOrg[],
  householdOrgIds: OrgId[],
  currentOrgId: number | undefined,
): ClientOrg | undefined {
  const householdOrgs = householdOrgIds.map((id) => orgs.find(({ id: orgId }) => orgId === id)).filter((org) => !!org);
  if (householdOrgs.length === 0) {
    return undefined;
  }
  return currentOrgId && householdOrgs.find(({ id }) => id === currentOrgId)
    ? householdOrgs.filter(({ id }) => id === currentOrgId)[0]
    : (householdOrgs.filter(({ type }) => type === OrgType.Church)[0] ?? orgs[0]);
}

export function useOrgEventsViewed() {
  const [mostRecentlyViewedOrgEvents, setMostRecentlyViewedOrgEvents] = useLocalStorage(
    LocalStorageKey.MostRecentlyViewedOrgEvents,
  );
  const [weeklySongList] = useLocalStorage(LocalStorageKey.WeeklySongList);
  const currentEvents = weeklySongList?.filter(({ type }) => type === SongListType.Event);
  const eventUpdatesViewed =
    currentEvents?.length === 0 || JSON.stringify(mostRecentlyViewedOrgEvents) === JSON.stringify(currentEvents);
  const setEventUpdatesViewed = useCallback(() => {
    setMostRecentlyViewedOrgEvents(currentEvents);
  }, [currentEvents, setMostRecentlyViewedOrgEvents]);
  return { eventUpdatesViewed, mostRecentlyViewedOrgEvents, setEventUpdatesViewed };
}

function clearOrgSongLists() {
  localStorageRemove(LocalStorageKey.WeeklySongList);
  localStorageRemove(LocalStorageKey.AllSongLists);
  localStorageRemove(LocalStorageKey.AllSongListsWrittenThrough);
}
